/**
 * 变换提交：把矩阵烘进图层像素（M2-14 / DESIGN.md §5.3）。
 *
 * 路径选择：
 * - 轴对齐（纯缩放/平移）→ wasm **Lanczos-3**，缩小 >2× 时 wasm 内部先做
 *   盒式预降采样（摩尔纹的正解，§5.3）。
 * - 含旋转/斜切 → wasm 仿射逆映射 + 双线性。各向异性高质量采样是后续任务。
 *
 * 提交是一段像素事务：touch 旧块+新块 → 快照 → 清旧写新。undo 由快照
 * 逐位还原（M1-08）。wasm 调用是同步的，AbortSignal 在关键点检查——
 * 事务写入前中止则文档不变（写入本身是最后一步）。
 */
import { TILE, tileKey, type LayerId } from '../tile/key';
import type { Rect } from './handles';
import { BYTES_PER_TILE, type TileStore } from '../tile/store';
import type { StrokeTransaction } from '../brush/buffer';
import { applyTo, invert, isAxisAligned, type Mat3 } from './mat3';
import {
  resample_lanczos3_rgba8,
  transform_bilinear_rgba8,
} from '../../wasm/core/photosmith_core';
import { ensureKernel } from '../wasm-kernel';

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted === true) throw new DOMException('操作已中止', 'AbortError');
}

/**
 * 翻转（镜像）源像素。
 *
 * `isAxisAligned` 只看 b/c 是否为零——镜像（a 或 d 为负）跟纯缩放一样
 * b=c=0，会被判成"轴对齐"走 Lanczos 快速路径，但 `resample_lanczos3_rgba8`
 * 只是个尺寸重采样，不认负值缩放，会把镜像悄悄退化成一次无效果的原样
 * resize（180° 旋转、水平/垂直翻转都会撞上——它们的 b/c 因为浮点误差
 * 也是"数值上的零"）。这里先手动镜像源像素，再喂给 Lanczos，
 * 相当于把"这次缩放是镜像的"这件事从矩阵里挑出来单独处理，
 * 缩放本身仍然享受 Lanczos 的质量。
 */
function mirrorRgba8(
  data: Uint8Array,
  width: number,
  height: number,
  flipH: boolean,
  flipV: boolean,
): Uint8Array {
  const out = new Uint8Array(data.length);
  for (let y = 0; y < height; y++) {
    const srcY = flipV ? height - 1 - y : y;
    for (let x = 0; x < width; x++) {
      const srcX = flipH ? width - 1 - x : x;
      const from = (srcY * width + srcX) * 4;
      const to = (y * width + x) * 4;
      out[to] = data[from]!;
      out[to + 1] = data[from + 1]!;
      out[to + 2] = data[from + 2]!;
      out[to + 3] = data[from + 3]!;
    }
  }
  return out;
}

/** 变换后的外接矩形（整数化，与文档相交）。 */
export function transformedBounds(bounds: Rect, matrix: Mat3, docWidth: number, docHeight: number): Rect {
  const corners = [
    applyTo(matrix, { x: bounds.x, y: bounds.y }),
    applyTo(matrix, { x: bounds.x + bounds.width, y: bounds.y }),
    applyTo(matrix, { x: bounds.x, y: bounds.y + bounds.height }),
    applyTo(matrix, { x: bounds.x + bounds.width, y: bounds.y + bounds.height }),
  ];
  const minX = Math.max(0, Math.floor(Math.min(...corners.map((c) => c.x))));
  const minY = Math.max(0, Math.floor(Math.min(...corners.map((c) => c.y))));
  const maxX = Math.min(docWidth, Math.ceil(Math.max(...corners.map((c) => c.x))));
  const maxY = Math.min(docHeight, Math.ceil(Math.max(...corners.map((c) => c.y))));
  return { x: minX, y: minY, width: Math.max(0, maxX - minX), height: Math.max(0, maxY - minY) };
}

export interface BakeResult {
  /** 新的图层外接矩形。 */
  readonly bounds: Rect;
  /** 受影响的文档块坐标（旧+新，渲染桥标脏用）。 */
  readonly dirty: ReadonlyArray<{ tx: number; ty: number }>;
}

export interface BakeOptions {
  readonly signal?: AbortSignal;
}

/**
 * 把 matrix 烘进图层像素。返回新 bounds；矩阵烘完即恒等（由调用方更新结构）。
 */
export async function bakeLayerTransform(
  store: TileStore,
  layer: LayerId,
  bounds: Rect,
  matrix: Mat3,
  docWidth: number,
  docHeight: number,
  transaction: StrokeTransaction,
  options: BakeOptions = {},
): Promise<BakeResult> {
  throwIfAborted(options.signal);
  await ensureKernel();
  throwIfAborted(options.signal);

  const inverse = invert(matrix);
  if (inverse === null) {
    throw new RangeError('变换矩阵退化（面积为零），拒绝提交。');
  }
  const target = transformedBounds(bounds, matrix, docWidth, docHeight);

  // 1. 展平源区域
  const srcW = Math.max(1, Math.ceil(bounds.width));
  const srcH = Math.max(1, Math.ceil(bounds.height));
  const source = new Uint8Array(srcW * srcH * 4);
  for (let y = 0; y < srcH; y++) {
    const docY = bounds.y + y;
    const ty = Math.floor(docY / TILE);
    for (let x = 0; x < srcW; x++) {
      const docX = bounds.x + x;
      const tile = store.get(tileKey(layer, Math.floor(docX / TILE), ty));
      const data = tile?.data;
      if (data == null) continue;
      const from = ((docY % TILE) * TILE + (docX % TILE)) * 4;
      const to = (y * srcW + x) * 4;
      source[to] = data[from]!;
      source[to + 1] = data[from + 1]!;
      source[to + 2] = data[from + 2]!;
      source[to + 3] = data[from + 3]!;
    }
  }
  throwIfAborted(options.signal);

  // 2. 重采样
  let output: Uint8Array;
  if (target.width === 0 || target.height === 0) {
    output = new Uint8Array(0);
  } else if (isAxisAligned(matrix)) {
    const prepared =
      matrix.a < 0 || matrix.d < 0
        ? mirrorRgba8(source, srcW, srcH, matrix.a < 0, matrix.d < 0)
        : source;
    output = resample_lanczos3_rgba8(prepared, srcW, srcH, target.width, target.height);
  } else {
    // 目标像素 → 源像素：先回文档坐标（逆矩阵），再回源缓冲坐标（减 bounds 原点）
    const inv = new Float32Array([
      inverse.a,
      inverse.b,
      inverse.c,
      inverse.d,
      inverse.e - bounds.x + inverse.a * target.x + inverse.c * target.y,
      inverse.f - bounds.y + inverse.b * target.x + inverse.d * target.y,
    ]);
    output = transform_bilinear_rgba8(source, srcW, srcH, target.width, target.height, inv);
  }
  throwIfAborted(options.signal);

  // 3. 计算受影响块（旧 ∪ 新），touch → capture → 清旧写新
  const dirtySet = new Map<string, { tx: number; ty: number }>();
  const addRect = (rect: Rect): void => {
    if (rect.width <= 0 || rect.height <= 0) return;
    const tx0 = Math.floor(rect.x / TILE);
    const ty0 = Math.floor(rect.y / TILE);
    const tx1 = Math.floor((rect.x + rect.width - 1) / TILE);
    const ty1 = Math.floor((rect.y + rect.height - 1) / TILE);
    for (let ty = ty0; ty <= ty1; ty++) {
      for (let tx = tx0; tx <= tx1; tx++) dirtySet.set(`${tx} ${ty}`, { tx, ty });
    }
  };
  addRect(bounds);
  addRect(target);
  for (const { tx, ty } of dirtySet.values()) transaction.touch(tileKey(layer, tx, ty));
  transaction.capture();

  for (const { tx, ty } of dirtySet.values()) {
    const key = tileKey(layer, tx, ty);
    const out = new Uint8Array(BYTES_PER_TILE);
    // 新内容与该块的交集
    const left = Math.max(target.x, tx * TILE);
    const top = Math.max(target.y, ty * TILE);
    const right = Math.min(target.x + target.width, (tx + 1) * TILE);
    const bottom = Math.min(target.y + target.height, (ty + 1) * TILE);
    let any = false;
    for (let y = top; y < bottom; y++) {
      for (let x = left; x < right; x++) {
        const from = ((y - target.y) * target.width + (x - target.x)) * 4;
        if (output[from + 3] === 0) continue;
        const to = ((y % TILE) * TILE + (x % TILE)) * 4;
        out[to] = output[from]!;
        out[to + 1] = output[from + 1]!;
        out[to + 2] = output[from + 2]!;
        out[to + 3] = output[from + 3]!;
        any = true;
      }
    }
    if (any) store.set(key, out);
    else store.clear(key);
  }
  return { bounds: target, dirty: [...dirtySet.values()] };
}
